import { useEffect, useState } from "react";
import { FormikProvider, useFormik } from "formik";
import { useSelector } from "react-redux";
import { CSVLink } from "react-csv";
import * as Yup from "yup";
import styled from "styled-components";
import { getRootProgramOptions } from "../../views/Admin/Program/manageProgram";
import { getStandardDate } from "../../views/Admin/Judging/Reports/manageHeaders";
import { RootState } from "../../store";
import { commafy } from "../../utils/commafy";
import config from "../../config";
import DropdownField, { DropdownOptions } from "../FormFields/DropdownField";
import Button from "../Button/Button";
import ResultsTable from "./ResultsTable";
import {
	EntryCheckout,
	ReviewStatusesAndAmount,
	getEntrantTrackingReport,
} from "./manageReports";

const LoadingSpinner = styled.img`
	width: 100px;
`;

const spinnerSrc = config.assets.loading.primary;

const removeQuotes = (value: string | number | null | undefined) => {
	if (typeof value !== "string") {
		return value ?? "";
	}

	return value.replace(/["]/g, "'");
};

const EntrantTracking = () => {
	const [rootProgramIDs, setRootProgramIDs] = useState<DropdownOptions[]>([]);
	const [selectedProgramId, setSelectedProgramId] = useState<
		string | number | undefined
	>();
	const [selectedProgramName, setSelectedProgramName] = useState<string>();
	const [checkoutCounts, setCheckoutCounts] = useState<EntryCheckout>();
	const [entrantTrackingRows, setEntrantTrackingRows] = useState<
		ReviewStatusesAndAmount[]
	>([]);
	const [loading, setLoading] = useState(false);
	const { settings } = useSelector((state: RootState) => state.settings);

	useEffect(() => {
		getRootProgramOptions().then((res) => {
			setRootProgramIDs(res.data);
		});
	}, []);

	const generateInformation = async () => {
		if (!selectedProgramId) {
			return;
		}

		setLoading(true);
		try {
			const { entryCheckout, reviewStatusesAndAmount } =
				await getEntrantTrackingReport(Number(selectedProgramId));
			setCheckoutCounts(entryCheckout);
			setEntrantTrackingRows(reviewStatusesAndAmount);
		} catch (error) {
			console.error("Failed to get entrant tracking report: ", error);
		}
		setLoading(false);
	};

	const formikProps = useFormik({
		initialValues: { rootProgramIDs },
		enableReinitialize: true,
		onSubmit: async () => {},
		validationSchema: Yup.object({}),
		validateOnBlur: false,
		validateOnChange: false,
	});

	const showCanonicalColumn = settings.featureFlags["Canonical Company"];
	const csvData = entrantTrackingRows.map((row) => {
		const csvRow: Record<string, string | number> = {
			Company: removeQuotes(row.entryProgram) as string,
			"Company ID": row.companyId ?? "",
		};

		if (showCanonicalColumn) {
			csvRow["Is Canonical"] = row.isCanonical ? "Yes" : "No";
		}

		[
			"incomplete",
			"readyForCheckout",
			"pending",
			"paymentPending",
			"paid",
			"completed",
		].forEach((status) => {
			const statusLabel = status.replace(/([A-Z])/g, " $1");
			const titleCaseStatus =
				statusLabel.charAt(0).toUpperCase() + statusLabel.slice(1);

			csvRow[`${titleCaseStatus} Entries`] = row[status].entries;
			csvRow[`${titleCaseStatus} Amount`] = removeQuotes(
				commafy(row[status].amount, settings.countryIso),
			) as string;
		});

		return csvRow;
	});

	return (
		<div>
			<FormikProvider value={formikProps}>
				<div className="flex items-center space-x-4 pb-5">
					<DropdownField
						name="rootProgram"
						options={rootProgramIDs}
						value={selectedProgramId || undefined}
						placeholder="Select a Program"
						onChange={(value) => {
							const selectedValue = Number(value.target.value);
							const selectedOption = rootProgramIDs.find(
								(option) => option.value === selectedValue,
							);
							setSelectedProgramId(selectedValue);
							setSelectedProgramName(String(selectedOption?.label || ""));
						}}
					/>
					<Button onClick={generateInformation}>Generate</Button>
				</div>
				{loading ? (
					<div className="pt-10 flex flex-col items-center justify-center">
						<div>Running Reports...</div>
						<LoadingSpinner src={spinnerSrc} />
					</div>
				) : checkoutCounts != null ? (
					<div>
						<div className="w-1/2">
							<ResultsTable
								data={checkoutCounts}
								disableExport={true}
								showTopScroll={false}
							/>
						</div>
						<div className="py-5 w-5">
							<CSVLink
								className="no-underline text-colorCopyLightLight"
								data={csvData}
								filename={
									(selectedProgramName ? selectedProgramName + " " : "") +
									"Entrant Tracking Report_" +
									getStandardDate() +
									".csv"
								}
							>
								<Button>Export to CSV</Button>
							</CSVLink>
						</div>
						<ResultsTable
							data={entrantTrackingRows}
							disableExport={true}
							firstColumnHeader="Company"
						/>
					</div>
				) : null}
			</FormikProvider>
		</div>
	);
};

export default EntrantTracking;
